import { MantineProvider } from '@mantine/core';
// prettier-ignore
import '@mantine/core/styles.css';

import '@mantine/charts/styles.css';
import '@mantine/code-highlight/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';

// Mantine plugins
import '@gfazioli/mantine-border-animate/styles.css';

import '@pitininja/cap-react-widget/dist/index.css';

import '@/assets/custom.scss';
import AppRoot from '@/components/AppRoot';
import AuthInitializer from '@/components/AuthInitializer';
import ViewClient from '@/components/Clients/ViewClient';
import CookieConsent from '@/components/CookieConsent';
import ErrorBoundary from '@/components/ErrorBoundary';
import Loading from '@/components/General/Loading';
import RequireBusiness from '@/components/RequireBusiness';
import RequireAuth from '@/components/RequireLogin';
import RequirePermission from '@/components/RequirePermission';
import ServicePaused from '@/components/ServicePaused';
import theme from '@/mantineTheme';
import { PageTitleProvider } from '@/providers/PageTitleProvider';
import QueryProvider from '@/providers/QueryProvider';
import { ModalsProvider } from '@mantine/modals';
import '@mantine/tiptap/styles.css';
import axios from 'axios';
import { lazy, Suspense, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router';

const Layout = lazy(() => import('@/components/Layout/Layout.jsx'));

const MinimalLayout = lazy(() => import('@/components/MinimalLayout.jsx'));
const OnboardingLayout = lazy(() => import('@/components/Onboarding/OnboardingLayout.jsx'));
const CreateBusiness = lazy(() => import('@/components/Businesses/CreateBusiness.jsx'));
const CreateBusinessOnboarding = lazy(() => import('@/components/Businesses/CreateBusinessOnboarding.jsx'));
const SelectBusiness = lazy(() => import('@/components/Businesses/SelectBusiness.jsx'));

const AuthLayout = lazy(() => import('@/components/Auth/Layout.jsx'));
const RegistrationGate = lazy(() => import('@/components/Auth/RegistrationGate.jsx'));
const Logout = lazy(() => import('@/components/Auth/Logout.jsx'));
const Register = lazy(() => import('@/components/Auth/Register.jsx'));
const BetaRegister = lazy(() => import('@/components/Auth/BetaRegister.jsx'));
const VerifyEmail = lazy(() => import('@/components/Auth/VerifyEmail.jsx'));
const Login = lazy(() => import('@/components/Auth/Login.jsx'));
const OTPForm = lazy(() => import('@/components/Auth/OTPForm.jsx'));
const ForgotPassword = lazy(() => import('@/components/Auth/ForgotPassword.jsx'));
const SetPassword = lazy(() => import('@/components/Auth/SetPassword.jsx'));
const AcceptInvite = lazy(() => import('@/components/Auth/AcceptInvite.jsx'));
const RejectInvite = lazy(() => import('@/components/Auth/RejectInvite.jsx'));

const Dashboard = lazy(() => import('@/components/Dashboard/DashboardRouter.jsx'));
const DemoDashboard = lazy(() => import('@/components/Dashboard/DemoDashboard.jsx'));

const Reports = lazy(() => import('@/components/Reports.jsx'));
const ARAgingReport = lazy(() => import('@/components/Reports/ARAgingReport.jsx'));
const SalesByClientReport = lazy(() => import('@/components/Reports/SalesByClientReport.jsx'));
const SalesByProductReport = lazy(() => import('@/components/Reports/SalesByProductReport.jsx'));
const TaxSummaryReport = lazy(() => import('@/components/Reports/TaxSummaryReport.jsx'));
const RevenueCollectionsReport = lazy(() => import('@/components/Reports/RevenueCollectionsReport.jsx'));

const Profile = lazy(() => import('@/components/Profile.jsx'));
const Business = lazy(() => import('@/components/Business.jsx'));
const SearchPage = lazy(() => import('@/components/Search.jsx'));

const Products = lazy(() => import('@/components/Products.jsx'));
const CreateProduct = lazy(() => import('@/components/Products/CreateProduct.jsx'));
const EditProduct = lazy(() => import('@/components/Products/EditProduct.jsx'));

const Emails = lazy(() => import('@/components/Emails.jsx'));
const EditDefaultEmailTemplate = lazy(() => import('@/components/Emails/EditDefaultEmailTemplate.jsx'));

const Users = lazy(() => import('@/components/Users.jsx'));

const ClientsList = lazy(() => import('@/components/Clients/ClientsList.jsx'));
const CreateClient = lazy(() => import('@/components/Clients/CreateClient.jsx'));
const EditClient = lazy(() => import('@/components/Clients/EditClient.jsx'));

const InvoicesList = lazy(() => import('@/components/Invoices/InvoicesList.jsx'));
const InvoicesEditRouter = lazy(() => import('@/components/Invoices/InvoicesEditRouter.jsx'));
const InvoicesManage = lazy(() => import('@/components/Invoices/InvoicesManage.jsx'));

const ScheduledEmailsCreate = lazy(() => import('@/components/Emails/ScheduledEmailsCreate.jsx'));
const ScheduledEmailsEdit = lazy(() => import('@/components/Emails/ScheduledEmailsEdit.jsx'));
const ScheduledEmailsTemplate = lazy(() => import('@/components/Emails/ScheduledEmailsTemplate.jsx'));

const Settings = lazy(() => import('@/components/Settings.jsx'));
const InvoiceDesign = lazy(() => import('@/components/Settings/InvoiceDesign.jsx'));
const OnlinePayments = lazy(() => import('@/components/Settings/OnlinePayments.jsx'));
const InvoiceCustomFields = lazy(() => import('@/components/Settings/InvoiceCustomFields.jsx'));

const Payment = lazy(() => import('@/components/Public/Payment.jsx'));

const NotFound = lazy(() => import('@/components/NotFound.jsx'));
const UnderReview = lazy(() => import('@/components/UnderReview.jsx'));

axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
axios.defaults.withCredentials = true;

// Setup axios interceptors for token refresh
// We'll initialize this in the AuthInitializer where we have access to the auth store

const App = () => {
  const LocationWatcher = () => {
    const location = useLocation();

    useEffect(() => {
      if (window.posthog) window.posthog.capture('$pageview');
      if (window._paq) window._paq.push(['trackPageView']);
      if (window.gtag) window.gtag('event', 'page_view', { page_path: location.pathname });
    }, [location.pathname]);

    // do stuff with location here, or pass it up via context or props
    return null;
  };

  if (import.meta.env.VITE_SERVICE_PAUSED === 'true') {
    return (
      <MantineProvider theme={theme}>
        <ServicePaused />
      </MantineProvider>
    );
  }

  return (
    <MantineProvider theme={theme}>
      <ErrorBoundary>
        <Toaster position="top-center" />
        <ModalsProvider>
          <QueryProvider>
            <BrowserRouter>
              <LocationWatcher />
              <PageTitleProvider>
                <AuthInitializer />
                <Routes>
                  <Route index element={<AppRoot />} />
                  <Route
                    path="/logout"
                    element={
                      <Suspense fallback={<Loading />}>
                        <Logout />
                      </Suspense>
                    }
                  />
                  {/* Public / Auth Routes */}
                  <Route
                    element={
                      <Suspense fallback={<Loading />}>
                        <AuthLayout />
                      </Suspense>
                    }
                  >
                    <Route
                      path="get-started"
                      element={
                        <Suspense fallback={<Loading />}>
                          <RegistrationGate>
                            <Register />
                          </RegistrationGate>
                        </Suspense>
                      }
                    />
                    <Route
                      path="closed-beta/sign-up"
                      element={
                        <Suspense fallback={<Loading />}>
                          <RegistrationGate>
                            <BetaRegister />
                          </RegistrationGate>
                        </Suspense>
                      }
                    />
                    <Route
                      path="verify-email"
                      element={
                        <Suspense fallback={<Loading />}>
                          <VerifyEmail />
                        </Suspense>
                      }
                    />
                    <Route
                      path="under-review"
                      element={
                        <Suspense fallback={<Loading />}>
                          <UnderReview />
                        </Suspense>
                      }
                    />
                    <Route
                      path="login"
                      element={
                        <Suspense fallback={<Loading />}>
                          <Login />
                        </Suspense>
                      }
                    />
                    <Route
                      path="login/two-factor"
                      element={
                        <Suspense fallback={<Loading />}>
                          <OTPForm />
                        </Suspense>
                      }
                    />
                    <Route
                      path="forgot-password"
                      element={
                        <Suspense fallback={<Loading />}>
                          <ForgotPassword />
                        </Suspense>
                      }
                    />
                    <Route
                      path="set-password"
                      element={
                        <Suspense fallback={<Loading />}>
                          <SetPassword />
                        </Suspense>
                      }
                    />
                    <Route
                      path="accept-invite/:token"
                      element={
                        <Suspense fallback={<Loading />}>
                          <AcceptInvite />
                        </Suspense>
                      }
                    />
                    <Route
                      path="reject-invite/:token"
                      element={
                        <Suspense fallback={<Loading />}>
                          <RejectInvite />
                        </Suspense>
                      }
                    />
                  </Route>

                  {/* Authenticated routes without business requirement */}
                  <Route
                    element={
                      <RequireAuth>
                        <Suspense fallback={<Loading />}>
                          <MinimalLayout />
                        </Suspense>
                      </RequireAuth>
                    }
                  >
                    <Route
                      path="/create-business"
                      element={
                        <Suspense fallback={<Loading />}>
                          <CreateBusiness />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/select-business"
                      element={
                        <Suspense fallback={<Loading />}>
                          <SelectBusiness />
                        </Suspense>
                      }
                    />
                  </Route>

                  {/* Onboarding shell routes */}
                  <Route
                    element={
                      <RequireAuth>
                        <Suspense fallback={<Loading />}>
                          <OnboardingLayout />
                        </Suspense>
                      </RequireAuth>
                    }
                  >
                    <Route
                      path="/onboarding/create-business"
                      element={
                        <Suspense fallback={<Loading />}>
                          <CreateBusinessOnboarding />
                        </Suspense>
                      }
                    />
                  </Route>

                  {/* Authenticated, Business-bound Routes */}
                  <Route
                    element={
                      <RequireAuth>
                        <RequireBusiness>
                          <Suspense fallback={<Loading />}>
                            <Layout />
                          </Suspense>
                        </RequireBusiness>
                      </RequireAuth>
                    }
                  >
                    <Route
                      path="/dashboard"
                      element={
                        <Suspense fallback={<Loading />}>
                          <Dashboard />
                        </Suspense>
                      }
                    />
                    {import.meta.env.DEV && (
                      <Route
                        path="/demo-dashboard"
                        element={
                          <Suspense fallback={<Loading />}>
                            <DemoDashboard />
                          </Suspense>
                        }
                      />
                    )}
                    <Route
                      path="/reports"
                      element={
                        <Suspense fallback={<Loading />}>
                          <Reports />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/reports/ar-aging"
                      element={
                        <Suspense fallback={<Loading />}>
                          <ARAgingReport />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/reports/sales-by-client"
                      element={
                        <Suspense fallback={<Loading />}>
                          <SalesByClientReport />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/reports/sales-by-product"
                      element={
                        <Suspense fallback={<Loading />}>
                          <SalesByProductReport />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/reports/tax-summary"
                      element={
                        <Suspense fallback={<Loading />}>
                          <TaxSummaryReport />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/reports/revenue-collections"
                      element={
                        <Suspense fallback={<Loading />}>
                          <RevenueCollectionsReport />
                        </Suspense>
                      }
                    />
                    {/* <Route
                    path="/dashboard2"
                    element={
                      <Suspense fallback={<Loading />}>
                        <AlternateDashboard />
                      </Suspense>
                    }
                  /> */}
                    {/* <Route path="/test" element={<Test />} /> */}
                    <Route path="/clients">
                      <Route
                        index
                        element={
                          <Suspense fallback={<Loading />}>
                            <ClientsList />
                          </Suspense>
                        }
                      />
                      <Route
                        path="create"
                        element={
                          <RequirePermission member>
                            <Suspense fallback={<Loading />}>
                              <CreateClient />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path=":id/edit"
                        element={
                          <RequirePermission member>
                            <Suspense fallback={<Loading />}>
                              <EditClient />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path=":id"
                        element={
                          <RequirePermission member>
                            <Suspense fallback={<Loading />}>
                              <ViewClient />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                    </Route>
                    <Route path="/invoices">
                      <Route
                        index
                        element={
                          <Suspense fallback={<Loading />}>
                            <InvoicesList />
                          </Suspense>
                        }
                      />
                      <Route
                        path="edit/:id/:tab?"
                        element={
                          <Suspense fallback={<Loading />}>
                            <InvoicesEditRouter />
                          </Suspense>
                        }
                      />
                      <Route
                        path=":id"
                        element={
                          <Suspense fallback={<Loading />}>
                            <InvoicesManage />
                          </Suspense>
                        }
                      />
                    </Route>

                    <Route path={'/products'}>
                      <Route
                        index
                        element={
                          <Suspense fallback={<Loading />}>
                            <Products />
                          </Suspense>
                        }
                      />
                      <Route
                        path=":id"
                        element={
                          <RequirePermission member>
                            <Suspense fallback={<Loading />}>
                              <EditProduct />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="create"
                        element={
                          <RequirePermission member>
                            <Suspense fallback={<Loading />}>
                              <CreateProduct />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                    </Route>
                    <Route path={'/emails'}>
                      <Route
                        index
                        element={
                          <RequirePermission admin>
                            <Suspense fallback={<Loading />}>
                              <Emails />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="scheduled-emails/create"
                        element={
                          <RequirePermission admin>
                            <Suspense fallback={<Loading />}>
                              <ScheduledEmailsCreate />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="scheduled-emails/settings/:id"
                        element={
                          <RequirePermission admin>
                            <Suspense fallback={<Loading />}>
                              <ScheduledEmailsEdit />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="scheduled-emails/template/:id"
                        element={
                          <RequirePermission admin>
                            <Suspense fallback={<Loading />}>
                              <ScheduledEmailsTemplate />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path=":key"
                        element={
                          <RequirePermission admin>
                            <Suspense fallback={<Loading />}>
                              <EditDefaultEmailTemplate />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                    </Route>
                    <Route path={'/users'}>
                      <Route
                        index
                        element={
                          <RequirePermission admin>
                            <Suspense fallback={<Loading />}>
                              <Users />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                    </Route>
                    <Route path="/settings">
                      <Route
                        index
                        element={
                          <RequirePermission admin>
                            <Suspense fallback={<Loading />}>
                              <Settings />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="invoice-custom-fields"
                        element={
                          <RequirePermission admin>
                            <Suspense fallback={<Loading />}>
                              <InvoiceCustomFields />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="invoice-design"
                        element={
                          <RequirePermission admin>
                            <Suspense fallback={<Loading />}>
                              <InvoiceDesign />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="online-payments"
                        element={
                          <RequirePermission admin>
                            <Suspense fallback={<Loading />}>
                              <OnlinePayments />
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                    </Route>
                    <Route
                      path="/business"
                      element={
                        <RequirePermission admin>
                          <Suspense fallback={<Loading />}>
                            <Business />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <Suspense fallback={<Loading />}>
                          <Profile />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/search"
                      element={
                        <Suspense fallback={<Loading />}>
                          <SearchPage />
                        </Suspense>
                      }
                    />
                  </Route>

                  {/* Public / Unauthenticated Routes */}
                  <Route
                    path="i"
                    element={
                      <RequireAuth>
                        <Suspense fallback={<Loading />}>
                          <MinimalLayout />
                        </Suspense>
                      </RequireAuth>
                    }
                  >
                    <Route
                      path="pay"
                      element={
                        <Suspense fallback={<Loading />}>
                          <Payment />
                        </Suspense>
                      }
                    />
                  </Route>

                  {/* 404 Route */}
                  <Route
                    path="/404"
                    element={
                      <Suspense fallback={<Loading />}>
                        <NotFound />
                      </Suspense>
                    }
                  />
                  {/* Catch-all for 404 */}
                  <Route
                    path="*"
                    element={
                      <Suspense fallback={<Loading />}>
                        <NotFound />
                      </Suspense>
                    }
                  />
                </Routes>
              </PageTitleProvider>
            </BrowserRouter>
          </QueryProvider>
        </ModalsProvider>
        <CookieConsent />
      </ErrorBoundary>
    </MantineProvider>
  );
};

export default App;
